import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Check, X } from "@phosphor-icons/react";
import { ModulePricing, TierPricing } from "../types";
import { RenderIcon } from "./IconPickerPopover";

interface FunctionalityMatrixProps {
  modulePricing: ModulePricing;
  tierPricing: TierPricing;
  /** If provided, enables interactive toggling */
  onModulePricingChange?: (mp: ModulePricing) => void;
}

export const FunctionalityMatrix = ({
  modulePricing,
  tierPricing,
  onModulePricingChange,
}: FunctionalityMatrixProps) => {
  const tierKeys = Object.keys(tierPricing);
  const modulesWithFuncs = Object.keys(modulePricing).filter(
    (k) =>
      modulePricing[k].enabled &&
      (modulePricing[k].functionalities?.length ?? 0) > 0
  );

  if (modulesWithFuncs.length === 0) return null;

  const isInteractive = !!onModulePricingChange;

  const toggleFunc = (modKey: string, funcId: string, tierKey: string) => {
    if (!onModulePricingChange) return;
    const mod = modulePricing[modKey];
    const funcs = (mod.functionalities || []).map((f) => {
      if (f.id !== funcId) return f;
      const current = f.tiers[tierKey] || "";
      // Cycle: empty → ✔ → empty
      const next = current === "" ? "\u2714" : "";
      return { ...f, tiers: { ...f.tiers, [tierKey]: next } };
    });
    onModulePricingChange({
      ...modulePricing,
      [modKey]: { ...mod, functionalities: funcs },
    });
  };

  return (
    <div className="space-y-10">
      <h2 className="text-2xl font-bold text-center tracking-tight">
        Volledige lijst met functionaliteiten
      </h2>

      {modulesWithFuncs.map((modKey) => {
        const mod = modulePricing[modKey];
        const funcs = mod.functionalities || [];

        return (
          <div key={modKey}>
            <div className="flex items-center gap-3 mb-4">
              <RenderIcon value={mod.icon} size={24} />
              <h3 className="text-lg font-bold">{mod.label}</h3>
            </div>

            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[280px]" />
                    {tierKeys.map((t) => (
                      <TableHead key={t} className="text-center w-[120px] font-bold text-sm">
                        {tierPricing[t]?.icon} {tierPricing[t]?.label}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {funcs.map((func) => (
                    <TableRow key={func.id}>
                      <TableCell className="text-sm text-gray-700">
                        {func.label}
                      </TableCell>
                      {tierKeys.map((t) => {
                        const val = func.tiers[t] || "";
                        const isCheck = val === "\u2714" || val === "\u2713" || val.toLowerCase() === "v";
                        const isEmpty = val === "";

                        return (
                          <TableCell key={t} className="text-center">
                            {isInteractive ? (
                              <button
                                onClick={() => toggleFunc(modKey, func.id, t)}
                                className={`w-7 h-7 rounded-md border inline-flex items-center justify-center transition-colors ${
                                  isCheck
                                    ? "bg-blue-50 border-blue-300 text-blue-600 hover:bg-blue-100"
                                    : isEmpty
                                      ? "bg-gray-50 border-gray-200 text-gray-300 hover:bg-gray-100 hover:text-gray-500"
                                      : "bg-amber-50 border-amber-200 text-amber-600"
                                }`}
                                title="Klik om te wisselen"
                              >
                                {isCheck ? (
                                  <Check size={14} weight="bold" />
                                ) : isEmpty ? (
                                  <X size={12} weight="bold" />
                                ) : (
                                  <span className="text-[9px] font-medium">{val.substring(0, 3)}</span>
                                )}
                              </button>
                            ) : (
                              <>
                                {isEmpty ? (
                                  <span className="text-gray-200">&mdash;</span>
                                ) : isCheck ? (
                                  <Check size={16} weight="bold" className="text-blue-600 mx-auto" />
                                ) : (
                                  <span className="text-sm text-gray-600">{val}</span>
                                )}
                              </>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        );
      })}
    </div>
  );
};
